class CollectionAPI extends HTMLElement {
  constructor() {
    super();
    
    // Check if filters or sort are active in URL
    const urlParams = new URLSearchParams(window.location.search);
    const hasFilterParams = Array.from(urlParams.keys()).some(key => 
      key.startsWith('pf_') || key === 'sort'
    );
    
    // Check if infinite scroll grid is present (new Liquid-based approach)
    const infiniteScrollGrid = document.querySelector('[data-infinite-scroll-grid]');
    
    // If no filters and infinite scroll is active, skip API initialization
    // Let Liquid-rendered products with infinite scroll work
    if (!hasFilterParams && infiniteScrollGrid) {
      console.log('[Collection API] No filters active, using Liquid products with infinite scroll');
      this.filtersActive = false;
      this.disabled = true;
      // Still setup filter listeners so filters can work when applied
      this.setupFilterListenersOnly();
      return;
    }
    
    this.filtersActive = hasFilterParams;
    this.disabled = false;
    
    this.API_BASE_URL = "https://services.mybcapps.com/bc-sf-filter/filter";
    // Get shop domain from data attribute or use default
    this.SHOP_DOMAIN = this.getAttribute("data-shop-domain") || "elie-tahari.myshopify.com";
    this.COLLECTION_RESULTS_GRID_ATTR = "grid-collection";
    this.PRODUCTS_PER_PAGE = 20; // Number of products to show initially and per "View More" click
    this.allProductHandles = []; // Store all product handles from filtered collection
    this.currentDisplayCount = 0; // Track how many products are currently displayed
    this.totalProducts = 0; // Total number of products found
    this.currentCollectionHandle = ''; // Store current collection handle
    this.currentCollectionId = 0; // Store current collection ID
    this.currentFilters = {}; // Store current applied filters
    this.currentPage = 1; // Track current page for pagination
    this.startingPage = 1; // Track the starting page (for cumulative loading from specific page)
    this.hasMorePages = true; // Track if there are more pages to load
    this.filterBaseUrl = ''; // Store the filter URL for pagination
    this.initialAPILoadComplete = false; // Track if initial API load completed (for bfcache handling)
    this.init();
  }
  
  /**
   * Setup only filter listeners when not fully initialized
   * This allows filters to work even when using Liquid products
   * When filters are applied, dynamically switch to API mode without reload
   */
  setupFilterListenersOnly() {
    const self = this;
    
    // Initialize properties needed for API mode
    this.API_BASE_URL = "https://services.mybcapps.com/bc-sf-filter/filter";
    this.SHOP_DOMAIN = this.getAttribute("data-shop-domain") || "elie-tahari.myshopify.com";
    this.COLLECTION_RESULTS_GRID_ATTR = "grid-collection";
    this.PRODUCTS_PER_PAGE = 20;
    this.allProductHandles = [];
    this.currentDisplayCount = 0;
    this.totalProducts = 0;
    this.currentCollectionHandle = '';
    this.currentCollectionId = 0;
    this.currentFilters = {};
    this.currentPage = 1;
    this.startingPage = 1;
    this.hasMorePages = true;
    this.filterBaseUrl = '';
    this.initialAPILoadComplete = false;
    
    // Extract collection handle
    const currentPath = window.location.pathname;
    const collectionMatch = currentPath.match(/\/collections\/([^\/\?]+)/);
    if (collectionMatch) {
      this.currentCollectionHandle = collectionMatch[1];
    }
    
    // Get collection ID from Boost config
    if (typeof boostPFSAppConfig !== 'undefined' && boostPFSAppConfig.general && boostPFSAppConfig.general.collection_id) {
      this.currentCollectionId = parseInt(boostPFSAppConfig.general.collection_id) || 0;
    }
    
    // Setup grid
    this.setupGrid();
    
    // Setup infinite scroll
    this.setupInfiniteScroll();
    
    // Setup full filter listeners (same as full mode)
    this.setupFilterListeners();
    
    // Setup product link tracking
    this.setupProductLinkTracking();
    
    // Mark as ready for filter handling
    this.disabled = false;
    this.filtersActive = false;
    
    console.log('[Collection API] Listener mode ready. Collection:', this.currentCollectionHandle, 'ID:', this.currentCollectionId);
  }

  init() {
    this.setupGrid();
    this.setupFilterListeners();
    this.extractCollectionHandle();
    
    // Check for URL parameters immediately and hide initial products if filters exist
    this.checkAndHideInitialProducts();
    
    // Check for URL parameters and apply filters on page load
    this.initializeFiltersFromURL();
    
    // Setup infinite scroll instead of View More button
    this.setupInfiniteScroll();

    // Setup product link click tracking for scroll restoration
    this.setupProductLinkTracking();

    // Continuously store scroll position while user scrolls on collection pages
    // This helps ensure accurate restoration when navigating back from PDP
    this.setupContinuousScrollTracking();
    
    // Check if we need to restore scroll position after products load
    this.checkScrollRestoration();
  }
  
  /**
   * Setup infinite scroll for loading more products
   */
  setupInfiniteScroll() {
    this.isLoadingMore = false;
    this.SCROLL_THRESHOLD = 2500; // Load more when within 2500px of bottom (very early trigger)
    this.prefetchCache = {}; // Cache for prefetched pages
    this.isPrefetching = false;
    
    console.log('[Collection API] Setting up infinite scroll with prefetch');
    
    // Fast scroll handler (50ms throttle for responsiveness)
    let scrollTimeout = null;
    const scrollHandler = () => {
      if (scrollTimeout) return;
      scrollTimeout = setTimeout(() => {
        scrollTimeout = null;
        this.checkAndLoadMore();
        this.checkAndPrefetch();
      }, 50);
    };
    
    window.addEventListener('scroll', scrollHandler, { passive: true });
    
    // Also check on resize
    window.addEventListener('resize', () => {
      setTimeout(() => this.checkAndLoadMore(), 100);
    }, { passive: true });
    
    // Initial check after products load
    setTimeout(() => {
      console.log('[Collection API] Initial infinite scroll check. hasMorePages:', this.hasMorePages, 'filterBaseUrl:', !!this.filterBaseUrl);
      this.checkAndLoadMore();
      this.checkAndPrefetch();
    }, 1000);
    
    // Check more frequently for smoother experience
    setInterval(() => {
      if (this.hasMorePages && !this.isLoadingMore) {
        this.checkAndLoadMore();
      }
    }, 500);
  }
  
  /**
   * Prefetch next page in background
   */
  async checkAndPrefetch() {
    if (this.isPrefetching || !this.hasMorePages || !this.filterBaseUrl) return;
    
    const nextPage = this.currentPage + 1;
    if (this.prefetchCache[nextPage]) return; // Already prefetched
    
    this.isPrefetching = true;
    
    try {
      const nextPageUrl = `${this.filterBaseUrl}&page=${nextPage}`;
      console.log('[Collection API] Prefetching page', nextPage);
      
      const response = await fetch(nextPageUrl);
      const data = await response.json();
      
      if (data.products && data.products.length > 0) {
        // Store prefetched data
        this.prefetchCache[nextPage] = {
          handles: data.products.map(p => p.handle),
          data: data
        };
        console.log('[Collection API] Prefetched page', nextPage, 'with', data.products.length, 'products');
      }
    } catch (error) {
      console.log('[Collection API] Prefetch error:', error);
    }
    
    this.isPrefetching = false;
  }
  
  /**
   * Check scroll position and load more if needed
   */
  checkAndLoadMore() {
    if (this.isLoadingMore) {
      return;
    }
    
    if (!this.hasMorePages) {
      return;
    }
    
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const distanceToBottom = documentHeight - (scrollTop + windowHeight);
    
    // Load more when near the bottom
    if (distanceToBottom <= this.SCROLL_THRESHOLD) {
      console.log('[Collection API] Near bottom, loading more. Distance:', distanceToBottom, 'Threshold:', this.SCROLL_THRESHOLD);
      this.loadMoreProductsViaInfiniteScroll();
    }
  }
  
  /**
   * Load more products via infinite scroll (silent, no button needed)
   * Uses prefetched data when available for instant loading
   */
  async loadMoreProductsViaInfiniteScroll() {
    if (this.isLoadingMore || !this.hasMorePages || !this.filterBaseUrl) return;
    
    const grid = document.querySelector(`[${this.COLLECTION_RESULTS_GRID_ATTR}]`) || 
                 document.querySelector('.boost-pfs-filter-products-new-bs') ||
                 document.querySelector('[data-infinite-scroll-grid]');
    if (!grid) return;
    
    this.isLoadingMore = true;
    
    try {
      // Increment page
      this.currentPage++;
      
      let productHandles;
      
      // Check if we have prefetched data for this page
      if (this.prefetchCache && this.prefetchCache[this.currentPage]) {
        console.log('[Collection API] Using prefetched data for page', this.currentPage);
        productHandles = this.prefetchCache[this.currentPage].handles;
        delete this.prefetchCache[this.currentPage]; // Clear used cache
      } else {
        // Fetch the page if not prefetched
        const nextPageUrl = `${this.filterBaseUrl}&page=${this.currentPage}`;
        console.log('[Collection API] Fetching page', this.currentPage);
        
        const response = await fetch(nextPageUrl);
        const data = await response.json();
        
        if (!data.products || data.products.length === 0) {
          this.hasMorePages = false;
          console.log('[Collection API] No more products to load');
          this.isLoadingMore = false;
          return;
        }
        
        productHandles = data.products.map(p => p.handle);
      }
      
      if (!productHandles || productHandles.length === 0) {
        this.hasMorePages = false;
        this.isLoadingMore = false;
        return;
      }
      
      // Load and append product cards (passing grid correctly)
      await this.loadProductCards(grid, productHandles, false); // append, not initial load
      
      // Check for more pages
      this.hasMorePages = productHandles.length === this.PRODUCTS_PER_PAGE && 
                         this.currentDisplayCount < this.totalProducts;
      
      console.log('[Collection API] Loaded', productHandles.length, 
                  'products. Total displayed:', this.currentDisplayCount,
                  'Has more:', this.hasMorePages);
      
      // Trigger event for other scripts (like image sliders)
      document.dispatchEvent(new CustomEvent('collection:products-loaded'));
      
      // Start prefetching next page immediately
      setTimeout(() => this.checkAndPrefetch(), 100);
      
    } catch (error) {
      console.error('[Collection API] Error loading more products:', error);
      this.currentPage--; // Revert page increment
    }
    
    this.isLoadingMore = false;
    
    // Check again immediately in case viewport is still near bottom
    setTimeout(() => this.checkAndLoadMore(), 50);
  }

  /**
   * Extract collection handle and ID from URL or Boost PFS config
   */
  extractCollectionHandle() {
    // Get collection handle from URL
    const currentPath = window.location.pathname;
    const collectionMatch = currentPath.match(/\/collections\/([^\/\?]+)/);
    
    if (collectionMatch) {
      this.currentCollectionHandle = collectionMatch[1];
    } else if (typeof boostPFSAppConfig !== 'undefined' && boostPFSAppConfig.general && boostPFSAppConfig.general.collection_handle) {
      this.currentCollectionHandle = boostPFSAppConfig.general.collection_handle;
    } else if (typeof Globals !== 'undefined' && Globals.collection && Globals.collection.handle) {
      this.currentCollectionHandle = Globals.collection.handle;
    }
    
    // Extract collection ID from Boost PFS config
    if (typeof boostPFSAppConfig !== 'undefined' && boostPFSAppConfig.general && boostPFSAppConfig.general.collection_id) {
      this.currentCollectionId = parseInt(boostPFSAppConfig.general.collection_id) || 0;
    }
    
    console.log('[Collection API] Collection handle:', this.currentCollectionHandle, 'Collection ID:', this.currentCollectionId);
  }

  /**
   * Check for URL filters and immediately hide initial products to show skeleton
   */
  checkAndHideInitialProducts() {
    // Check if URL has filter parameters
    const urlParams = new URLSearchParams(window.location.search);
    const hasFilterParams = Array.from(urlParams.keys()).some(key => 
      key.startsWith('pf_pt_category') || 
      key.startsWith('pf_pt_product_type') || 
      key.startsWith('pf_opt_size') || 
      key.startsWith('pf_t_color') || 
      key.startsWith('pf_v_vendor') || 
      key.startsWith('pf_c_collection') || 
      key.startsWith('pf_t_tag') || 
      key.startsWith('pf_p_price')
    );

    // Immediately hide initial products and show skeleton
    // Always load from API, so always show skeleton to replace server-rendered products
    const hideInitialProducts = () => {
      const grid = document.querySelector(`[${this.COLLECTION_RESULTS_GRID_ATTR}]`) || 
                   document.querySelector('.boost-pfs-filter-products-new-bs');
      
      if (grid) {
        // Always show skeleton when loading from API (even without filters)
        // This replaces server-rendered products with API-loaded products
        const hasProducts = grid.children.length > 0 && 
                           !grid.querySelector('.collection-skeleton-item') &&
                           grid.querySelector('.ProductItem, .Carousel__Cell');
        
        // Show skeleton if there are filter params OR if there are server-rendered products to replace
        if (hasFilterParams || hasProducts) {
          // Show skeleton loader immediately (this replaces innerHTML, so no need to hide first)
          this.showSkeletonLoader(grid);
          
          // Ensure grid is visible
          grid.style.opacity = '1';
          grid.style.transition = '';
          
          if (hasFilterParams) {
            console.log('[Collection API] Hidden initial products and showing skeleton for URL filters');
          } else {
            console.log('[Collection API] Showing skeleton to replace server-rendered products with API-loaded products');
            // Skeleton will be replaced when API products load (no auto-hide)
          }
        } else {
          // No products or filters yet – still show skeleton and keep it visible
          // until API products are loaded and replace it.
          this.showSkeletonLoader(grid);
          grid.style.opacity = '1';
          grid.style.transition = '';
          console.log('[Collection API] Showing skeleton and waiting for API products');
        }
      } else {
        // Retry if grid not found yet
        if (this.hideProductsRetries < 5) {
          this.hideProductsRetries = (this.hideProductsRetries || 0) + 1;
          setTimeout(hideInitialProducts, 100);
        }
      }
    };

    // Reset retry counter
    this.hideProductsRetries = 0;

    // Try to hide immediately, or wait for DOM
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(hideInitialProducts, 50);
      });
    } else {
      setTimeout(hideInitialProducts, 50);
    }
  }

  /**
   * Initialize filters from URL parameters on page load
   * Directly calls the API with URL filters without checking filter tree
   */
  initializeFiltersFromURL() {
    // Check if URL has filter parameters
    const urlParams = new URLSearchParams(window.location.search);
    const hasFilterParams = Array.from(urlParams.keys()).some(key => 
      key.startsWith('pf_pt_category') || 
      key.startsWith('pf_pt_product_type') || 
      key.startsWith('pf_opt_size') || 
      key.startsWith('pf_t_color') || 
      key.startsWith('pf_v_vendor') || 
      key.startsWith('pf_c_collection') || 
      key.startsWith('pf_t_tag') || 
      key.startsWith('pf_p_price')
    );

    if (!hasFilterParams) {
      return; // No filter parameters in URL
    }

    // Prevent double initialization
    if (this.urlFiltersInitialized) {
      return;
    }
    this.urlFiltersInitialized = true;

    console.log('[Collection API] Found filter parameters in URL, applying filters directly...');

    // Wait for collection handle to be available and grid to be ready
    const applyFiltersFromURL = () => {
      // Ensure we have collection handle
      if (!this.currentCollectionHandle) {
        this.extractCollectionHandle();
      }
      
      if (!this.currentCollectionHandle) {
        // Retry if collection handle not found yet (max 10 retries = 2 seconds)
        if (this.urlFilterRetries < 10) {
          this.urlFilterRetries = (this.urlFilterRetries || 0) + 1;
          setTimeout(applyFiltersFromURL, 200);
        }
        return;
      }

      // Check if grid exists
      const grid = document.querySelector(`[${this.COLLECTION_RESULTS_GRID_ATTR}]`) || 
                   document.querySelector('.boost-pfs-filter-products-new-bs');
      
      if (!grid) {
        // Retry if grid not found yet (max 10 retries = 2 seconds)
        if (this.urlFilterRetries < 10) {
          this.urlFilterRetries = (this.urlFilterRetries || 0) + 1;
          setTimeout(applyFiltersFromURL, 200);
        }
        return;
      }

      // Extract filters from URL
      const urlFilters = this.extractFiltersFromURL();
      
      if (Object.keys(urlFilters).length === 0) {
        return;
      }

      console.log('[Collection API] Filters from URL:', urlFilters);
      console.log('[Collection API] Collection handle:', this.currentCollectionHandle);

      // Ensure skeleton is showing (in case checkAndHideInitialProducts didn't run yet)
      if (!grid.querySelector('.collection-skeleton-item')) {
        this.showSkeletonLoader(grid);
      }

      // Store filters and directly call performCollectionFilter
      // This will fetch products from API with the filters and display them
      this.currentFilters = urlFilters;
      this.performCollectionFilter(this.currentCollectionHandle, urlFilters);
    };

    // Reset retry counter
    this.urlFilterRetries = 0;

    // Start applying filters after a short delay to ensure everything is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(applyFiltersFromURL, 300);
      });
    } else {
      setTimeout(applyFiltersFromURL, 300);
    }
  }

  /**
   * Extract filters from URL parameters
   * @returns {Object} - Object containing filter values from URL
   */
  extractFiltersFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const filters = {};

    // Category - pf_pt_category or pf_pt_category[]
    const categories = urlParams.getAll('pf_pt_category');
    if (categories.length > 0) {
      filters.category = categories.map(cat => decodeURIComponent(cat));
    }
    
    // Also check array format
    const categoryArray = urlParams.getAll('pf_pt_category[]');
    if (categoryArray.length > 0) {
      if (!filters.category) filters.category = [];
      categoryArray.forEach(cat => {
        const decoded = decodeURIComponent(cat);
        if (!filters.category.includes(decoded)) {
          filters.category.push(decoded);
        }
      });
    }

    // Product Type - pf_pt_product_type or pf_pt_product_type[]
    const productTypes = urlParams.getAll('pf_pt_product_type');
    const productTypesArray = urlParams.getAll('pf_pt_product_type[]');
    const allProductTypes = [...productTypes, ...productTypesArray];
    if (allProductTypes.length > 0) {
      filters.product_type = allProductTypes.map(type => decodeURIComponent(type));
    }

    // Size - pf_opt_size or pf_opt_size[]
    const sizes = urlParams.getAll('pf_opt_size');
    const sizesArray = urlParams.getAll('pf_opt_size[]');
    const allSizes = [...sizes, ...sizesArray];
    if (allSizes.length > 0) {
      filters.size = allSizes.map(size => decodeURIComponent(size));
    }

    // Color - pf_t_color or pf_t_color[]
    const colors = urlParams.getAll('pf_t_color');
    const colorsArray = urlParams.getAll('pf_t_color[]');
    const allColors = [...colors, ...colorsArray];
    if (allColors.length > 0) {
      filters.color = allColors.map(color => {
        const decoded = decodeURIComponent(color);
        // Remove COLOR_ prefix if present
        return decoded.replace(/^COLOR_/i, '');
      });
    }

    // Vendor - pf_v_vendor or pf_v_vendor[]
    const vendors = urlParams.getAll('pf_v_vendor');
    const vendorsArray = urlParams.getAll('pf_v_vendor[]');
    const allVendors = [...vendors, ...vendorsArray];
    if (allVendors.length > 0) {
      filters.vendor = allVendors.map(vendor => decodeURIComponent(vendor));
    }

    // Collection - pf_c_collection or pf_c_collection[]
    const collections = urlParams.getAll('pf_c_collection');
    const collectionsArray = urlParams.getAll('pf_c_collection[]');
    const allCollections = [...collections, ...collectionsArray];
    if (allCollections.length > 0) {
      filters.collection = allCollections.map(col => decodeURIComponent(col));
    }

    // Tag - pf_t_tag or pf_t_tag[]
    const tags = urlParams.getAll('pf_t_tag');
    const tagsArray = urlParams.getAll('pf_t_tag[]');
    const allTags = [...tags, ...tagsArray];
    if (allTags.length > 0) {
      filters.tag = allTags.map(tag => decodeURIComponent(tag));
    }

    // Price - pf_p_price or pf_p_price[]
    const prices = urlParams.getAll('pf_p_price');
    const pricesArray = urlParams.getAll('pf_p_price[]');
    const allPrices = [...prices, ...pricesArray];
    if (allPrices.length > 0) {
      filters.price = decodeURIComponent(allPrices[0]);
    }

    // Sort - map to API format (check both 'sort' and 'sort_by' for compatibility)
    const sort = urlParams.get('sort') || urlParams.get('sort_by');
    if (sort) {
      const sortValue = decodeURIComponent(sort);
      // Map sort values to API format
      const sortMap = {
        'price-ascending': 'price-ascending',
        'price-descending': 'price-descending',
        'best-selling': 'best-selling',
        'created-descending': 'created-descending',
        'date-descending': 'created-descending',
        'title-ascending': 'title-ascending',
        'title-descending': 'title-descending',
        'manual': 'manual',
        'featured': 'manual'
      };
      filters.sort = sortMap[sortValue] || sortValue;
    }

    return filters;
  }

  /**
   * Initialize View More button for server-rendered products when there are no filters
   */
  async initializeViewMoreForInitialProducts() {
    // Check if URL has filter parameters
    const urlParams = new URLSearchParams(window.location.search);
    const hasFilterParams = Array.from(urlParams.keys()).some(key => 
      key.startsWith('pf_pt_category') || 
      key.startsWith('pf_pt_product_type') || 
      key.startsWith('pf_opt_size') || 
      key.startsWith('pf_t_color') || 
      key.startsWith('pf_v_vendor') || 
      key.startsWith('pf_c_collection') || 
      key.startsWith('pf_t_tag') || 
      key.startsWith('pf_p_price')
    );

    // Only initialize if there are NO filter parameters
    if (hasFilterParams) {
      return; // Filters will be handled by initializeFiltersFromURL
    }

    // Check for page parameter in URL
    const pageParam = urlParams.get('page');
    const urlPage = pageParam ? parseInt(pageParam, 10) : null;

    // Wait for collection handle and grid to be ready
    const initViewMore = async () => {
      // Ensure we have collection handle
      if (!this.currentCollectionHandle) {
        this.extractCollectionHandle();
      }
      
      if (!this.currentCollectionHandle) {
        // Retry if collection handle not found yet
        setTimeout(initViewMore, 200);
        return;
      }

      // Check if grid exists
      const grid = document.querySelector(`[${this.COLLECTION_RESULTS_GRID_ATTR}]`) || 
                   document.querySelector('.boost-pfs-filter-products-new-bs');
      
      if (!grid) {
        // Retry if grid not found yet
        setTimeout(initViewMore, 200);
        return;
      }

      // If URL has page parameter, load all pages from 1 to that page (cumulative)
      if (urlPage && urlPage > 1) {
        console.log('[Collection API] Page parameter found in URL:', urlPage, '- Loading all pages from 1 to', urlPage, '(cumulative)');
        
        try {
          // Build filter URL for pagination (no filters)
          const emptyFilters = {}; // No filters
          this.currentFilters = emptyFilters;
          this.buildFilterUrl(this.currentCollectionHandle, emptyFilters);
          
          // Clear grid and show skeleton
          this.showSkeletonLoader(grid);
          
          // Fetch all pages from 1 to urlPage (cumulative)
          let allHandles = [];
          let totalProducts = 0;
          
          for (let page = 1; page <= urlPage; page++) {
            const pageData = await this.fetchPage(this.currentCollectionHandle, emptyFilters, page);
            
            if (!pageData) {
              console.warn('[Collection API] No data returned from page', page);
              break;
            }
            
            // Get total products from first page
            if (page === 1) {
              totalProducts = pageData.totalProducts || 0;
              this.totalProducts = totalProducts;
            }
            
            // Accumulate handles from all pages
            if (pageData.handles && pageData.handles.length > 0) {
              allHandles = allHandles.concat(pageData.handles);
            }
          }
          
          if (allHandles.length === 0) {
            console.warn('[Collection API] No products loaded from pages 1 to', urlPage);
            this.hideSkeletonLoader(grid);
            return;
          }

          // Set up pagination state
          this.currentPage = urlPage;
          this.startingPage = 1; // Started from page 1
          this.allProductHandles = allHandles;
          
          // Load all accumulated products (cumulative from page 1 to urlPage)
          await this.loadProductCards(grid, allHandles, true);
          
          this.currentDisplayCount = allHandles.length;
          
          // Check if there are more pages
          this.hasMorePages = this.currentDisplayCount < this.totalProducts;

          console.log('[Collection API] Loaded pages 1 to', urlPage, 'products (cumulative). Total:', this.totalProducts, 'Displayed:', this.currentDisplayCount);

          // Update View More button
          this.updateViewMoreButton();
          
          // Note: Scroll restoration is handled inside loadProductCards when isInitialLoad=true
        } catch (error) {
          console.error('[Collection API] Error loading pages from URL:', error);
        }
        return;
      }

      // No page parameter - load page 1 from API (always load from API, not server-rendered)
      console.log('[Collection API] Loading page 1 from API (no page parameter in URL)');

      try {
        // Build filter URL for pagination (no filters)
        const emptyFilters = {}; // No filters
        this.currentFilters = emptyFilters;
        this.buildFilterUrl(this.currentCollectionHandle, emptyFilters);
        
        // Show skeleton loader to replace server-rendered products
        this.showSkeletonLoader(grid);
        
        // Fetch first page from API
        const firstPageData = await this.fetchPage(this.currentCollectionHandle, emptyFilters, 1);
        
        if (!firstPageData || firstPageData.handles.length === 0) {
          console.warn('[Collection API] No data returned from first page');
          this.hideSkeletonLoader(grid);
          this.showEmptyState(grid);
          return;
        }

        // Set up pagination state
        this.totalProducts = firstPageData.totalProducts || 0;
        this.allProductHandles = firstPageData.handles;
        this.currentPage = 1;
        this.startingPage = 1;
        
        // Load products from API (this will replace server-rendered products)
        await this.loadProductCards(grid, firstPageData.handles, true);
        
        this.currentDisplayCount = firstPageData.handles.length;
        
        // Check if there are more pages
        this.hasMorePages = firstPageData.handles.length === this.PRODUCTS_PER_PAGE && 
                           this.currentDisplayCount < this.totalProducts;

        console.log('[Collection API] Loaded page 1 from API. Total:', this.totalProducts, 'Displayed:', this.currentDisplayCount, 'Has more pages:', this.hasMorePages);

        // Update View More button
        this.updateViewMoreButton();
        
        // Note: Scroll restoration is handled inside loadProductCards when isInitialLoad=true
      } catch (error) {
        console.error('[Collection API] Error loading page 1 from API:', error);
        this.hideSkeletonLoader(grid);
      }
    };

    // Start initialization after a delay to ensure everything is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(initViewMore, 500);
      });
    } else {
      setTimeout(initViewMore, 500);
    }
  }

  setupGrid() {
    const grid = document.querySelector(`[${this.COLLECTION_RESULTS_GRID_ATTR}]`);
    if (!grid) {
      // Try alternative selector for collection page
      const altGrid = document.querySelector('.boost-pfs-filter-products-new-bs');
      if (altGrid && window.location.pathname.includes('/collections/')) {
        altGrid.setAttribute(this.COLLECTION_RESULTS_GRID_ATTR, '');
      }
    }
  }

  /**
   * Setup listeners for Boost PFS filter changes
   */
  setupFilterListeners() {
    // Use event delegation on document to catch dynamically added filter elements
    document.addEventListener('click', (e) => {
      const target = e.target;
      
      // Handle View Results button click in collection drawer
      if (target.id === 'collection-filter-view-results' || target.closest('#collection-filter-view-results')) {
        e.preventDefault();
        e.stopPropagation();
        // Call immediately - View Results should always trigger filter
        this.onFilterChange();
      }
      
      // Handle View Results button click in alternative drawer
      if (target.id === 'collection-filter-view-results-alt' || target.closest('#collection-filter-view-results-alt')) {
        e.preventDefault();
        e.stopPropagation();
        // Call immediately - View Results should always trigger filter
        this.onFilterChange();
      }
      
      // Handle View Results button click in old drawer (backup)
      if (target.closest('#custom-drawer .collection-filter-view-results-button')) {
        setTimeout(() => this.onFilterChange(), 100);
      }
      
      // Handle View Results button in collection-filter-drawer (fallback)
      if (target.closest('#collection-filter-drawer .collection-filter-view-results-button')) {
        setTimeout(() => this.onFilterChange(), 100);
      }
      
      // Handle Apply button clicks within filter options
      if (target.classList.contains('boost-pfs-filter-apply-button')) {
        setTimeout(() => this.onFilterChange(), 200);
      }
      
      // Handle Apply All button click
      if (target.classList.contains('boost-pfs-filter-apply-all-button')) {
        setTimeout(() => this.onFilterChange(), 200);
      }
      
      // Handle Clear All button clicks
      if (target.classList.contains('boost-pfs-filter-clear-all')) {
        // Wait longer for Boost PFS to clear all filters and update DOM
        setTimeout(() => this.onFilterChange(), 400);
      }
      
      // Handle individual Clear button clicks (within refine-by items or filter options)
      if (target.classList.contains('boost-pfs-filter-clear') || target.closest('.boost-pfs-filter-clear')) {
        // Don't call onFilterChange here - let Boost PFS update DOM first
        // The onFilterChange will be called after a delay to ensure DOM is updated
        setTimeout(() => this.onFilterChange(), 400);
      }
      
      // Handle refine-by item link clicks (the entire refine-by-item is clickable)
      // This should trigger filter update but NOT close the drawer
      const refineByItemLink = target.closest('.refine-by-item a[tabindex="0"]');
      if (refineByItemLink && !target.closest('.boost-pfs-filter-clear')) {
        // Prevent default navigation
        e.preventDefault();
        e.stopPropagation();
        // Wait longer for Boost PFS to update the DOM, then update filters
        setTimeout(() => this.onFilterChange(), 400);
        return;
      }
      
      // Handle refine-by item removal (clicking on applied filter tags)
      if (target.closest('.refine-by-item')) {
        // Check if it's the clear button - if so, we already handled it above
        const isClearButton = target.classList.contains('boost-pfs-filter-clear') || target.closest('.boost-pfs-filter-clear');
        if (!isClearButton) {
          // It's the refine-by-item link itself
          const refineByItem = target.closest('.refine-by-item');
          if (refineByItem) {
            // Wait longer for Boost PFS to update the DOM
            setTimeout(() => this.onFilterChange(), 400);
          }
        }
      }
      
      // Handle Show Results button click (mobile)
      if (target.classList.contains('boost-pfs-filter-show-result')) {
        setTimeout(() => this.onFilterChange(), 100);
      }

      // Handle sort button clicks - apply immediately via API (no View Results needed)
      if (target.closest('.boost-pfs-filter-top-sorting .Popover__Value')) {
        const sortBtn = target.closest('.Popover__Value');
        const sortValue = sortBtn ? sortBtn.getAttribute('data-value') : null;
        if (sortValue) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation(); // Prevent Boost PFS from also handling this
          
          // Map sort values to API format (only the 4 needed: price-ascending, price-descending, best-selling, created-descending)
          const sortMap = {
            'price-ascending': 'price-ascending',
            'price-descending': 'price-descending',
            'best-selling': 'best-selling',
            'created-descending': 'created-descending',
            'date-descending': 'created-descending',
            'title-ascending': 'title-ascending',
            'title-descending': 'title-descending',
            'manual': 'manual',
            'featured': 'manual'
          };
          
          const apiSortValue = sortMap[sortValue] || sortValue;
          
          console.log('[Collection API] Sort clicked:', sortValue, '-> API value:', apiSortValue);
          
          // Update active state immediately
          const sortContainer = sortBtn.closest('.boost-pfs-filter-top-sorting');
          if (sortContainer) {
            sortContainer.querySelectorAll('.Popover__Value').forEach(function(btn) {
              btn.classList.remove('is-selected', 'active');
              btn.removeAttribute('aria-selected');
            });
            sortBtn.classList.add('is-selected', 'active');
            sortBtn.setAttribute('aria-selected', 'true');
          }
          
          // Ensure collection handle is available
          if (!this.currentCollectionHandle) {
            this.extractCollectionHandle();
          }
          
          if (!this.currentCollectionHandle) {
            console.warn('[Collection API] No collection handle found for sorting');
            return;
          }
          
          // Extract current filters (preserve existing filters) - use cached if available
          const currentFilters = Object.keys(this.currentFilters).length > 0 
            ? Object.assign({}, this.currentFilters) 
            : this.extractFiltersFromDOM();
          
          // Set sort value
          currentFilters.sort = apiSortValue;
          this.currentFilters = currentFilters;
          
          console.log('[Collection API] Applying sort with filters:', this.currentFilters);
          
          // Update URL immediately - use 'sort' parameter (API format), remove 'sort_by' if exists
          if (window.history && window.history.replaceState) {
            const url = new URL(window.location);
            // Remove both parameters first to avoid duplicates
            url.searchParams.delete('sort');
            url.searchParams.delete('sort_by');
            // Then set only 'sort' parameter
            if (apiSortValue && apiSortValue !== 'manual' && apiSortValue !== 'featured') {
              url.searchParams.set('sort', apiSortValue);
            }
            window.history.replaceState({}, '', url);
          }
          
          // Apply sort immediately via API (no delay, no waiting for View Results)
          this.performCollectionFilter(this.currentCollectionHandle, this.currentFilters);
        }
      }
    });

    // Also observe for DOM changes to detect when filters are modified by Boost PFS
    this.observeFilterChanges();
  }

  /**
   * Observe filter tree for changes (when Boost PFS updates the filter state)
   */
  observeFilterChanges() {
    // Create a debounced version of filter change handler
    let filterChangeTimeout = null;
    
    const debouncedFilterChange = () => {
      if (filterChangeTimeout) {
        clearTimeout(filterChangeTimeout);
      }
      filterChangeTimeout = setTimeout(() => {
        // Only trigger if we have a collection handle
        if (this.currentCollectionHandle) {
          const newFilters = this.extractFiltersFromDOM();
          const filtersChanged = JSON.stringify(newFilters) !== JSON.stringify(this.currentFilters);
          if (filtersChanged) {
            this.currentFilters = newFilters;
          }
        }
      }, 200);
    };

    // Try to observe the filter tree, retry if not found (Boost PFS loads dynamically)
    const setupObserver = () => {
      const filterTree = document.querySelector('.boost-pfs-filter-tree');
      if (filterTree && filterTree.children.length > 0) {
        const observer = new MutationObserver((mutations) => {
          // Check if the mutation involves selected items
          const hasSelectionChange = mutations.some(mutation => {
            return mutation.type === 'attributes' && mutation.attributeName === 'class';
          });
          
          if (hasSelectionChange) {
            debouncedFilterChange();
          }
        });
        
        observer.observe(filterTree, {
          attributes: true,
          subtree: true,
          attributeFilter: ['class']
        });
      } else {
        // Retry after a delay if filter tree is not ready
        setTimeout(setupObserver, 500);
      }
    };
    
    // Start observer setup
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => setTimeout(setupObserver, 500));
    } else {
      setTimeout(setupObserver, 500);
    }
  }

  /**
   * Handle filter change - extract filters and re-run collection filter
   */
  onFilterChange() {
    if (!this.currentCollectionHandle) {
      this.extractCollectionHandle();
    }
    
    if (!this.currentCollectionHandle) {
      console.warn('[Collection API] No collection handle found');
      return;
    }
    
    // Wait for Boost PFS to fully update the DOM after filter removal
    // Use requestAnimationFrame to wait for DOM updates, then extract filters
    requestAnimationFrame(() => {
      setTimeout(() => {
        // Extract current filters from DOM (after Boost PFS has updated it)
        const newFilters = this.extractFiltersFromDOM();
        
        // Always update and perform filter (don't check if changed, as DOM might not be fully updated yet)
        this.currentFilters = newFilters;
        
        console.log('[Collection API] Filters after change:', this.currentFilters);
        
        // Perform collection filter with filters
        this.performCollectionFilter(this.currentCollectionHandle, this.currentFilters);
      }, 300); // Wait for Boost PFS to update DOM
    });
  }

  /**
   * Extract selected filters from the Boost PFS filter DOM
   * @returns {Object} - Object containing all selected filter values
   */
  extractFiltersFromDOM() {
    const filters = {};
    
    // Get all filter options in the filter tree
    const filterOptions = document.querySelectorAll('.boost-pfs-filter-tree .boost-pfs-filter-option');
    
    filterOptions.forEach(option => {
      // Get the filter title/label to determine filter type
      const titleEl = option.querySelector('.boost-pfs-filter-option-title-text');
      if (!titleEl) return;
      
      const filterLabel = titleEl.textContent.trim().toLowerCase();
      
      // Find selected items - Boost PFS adds 'selected' class to li elements
      const selectedItems = option.querySelectorAll('li.selected, .boost-pfs-filter-option-item.selected');
      if (selectedItems.length === 0) return;
      
      const values = [];
      selectedItems.forEach(item => {
        // Try multiple selectors for the value
        const valueEl = item.querySelector('.boost-pfs-filter-option-value') || 
                       item.querySelector('.boost-pfs-filter-button') ||
                       item.querySelector('[data-value]');
        if (valueEl) {
          const value = valueEl.getAttribute('data-value') || valueEl.textContent.trim();
          if (value) values.push(value);
        }
      });
      
      if (values.length === 0) return;
      
      // Map filter labels to API parameter names
      if (filterLabel.includes('product type') || filterLabel.includes('product_type') || filterLabel.includes('type')) {
        filters.product_type = values;
      } else if (filterLabel.includes('size')) {
        filters.size = values;
      } else if (filterLabel.includes('color') || filterLabel.includes('colour')) {
        filters.color = values;
      } else if (filterLabel.includes('vendor') || filterLabel.includes('brand')) {
        filters.vendor = values;
      } else if (filterLabel.includes('collection')) {
        filters.collection = values;
      } else if (filterLabel.includes('tag')) {
        filters.tag = values;
      } else if (filterLabel.includes('category')) {
        filters.category = values;
      }
    });
    
    // Extract Price filter (range)
    const priceOption = document.querySelector('.boost-pfs-filter-option[class*="price"]');
    if (priceOption) {
      const minInput = priceOption.querySelector('.boost-pfs-filter-option-range-amount-min, input[class*="min"]');
      const maxInput = priceOption.querySelector('.boost-pfs-filter-option-range-amount-max, input[class*="max"]');
      
      if (minInput && maxInput) {
        const minVal = minInput.value.replace(/[^0-9.]/g, '');
        const maxVal = maxInput.value.replace(/[^0-9.]/g, '');
        
        if (minVal && maxVal) {
          // Check if these are different from the default range
          const slider = priceOption.querySelector('.noUi-target');
          if (slider) {
            const minHandle = slider.querySelector('.noUi-handle-lower');
            const maxHandle = slider.querySelector('.noUi-handle-upper');
            
            if (minHandle && maxHandle) {
              const ariaMin = parseFloat(minHandle.getAttribute('aria-valuemin'));
              const ariaMax = parseFloat(minHandle.getAttribute('aria-valuemax'));
              const currentMin = parseFloat(minHandle.getAttribute('aria-valuenow'));
              const currentMax = parseFloat(maxHandle.getAttribute('aria-valuenow'));
              
              // Only add price filter if values are different from min/max range
              if (currentMin > ariaMin || currentMax < ariaMax) {
                filters.price = `${currentMin.toFixed(2)}:${currentMax.toFixed(2)}`;
              }
            }
          } else if (parseFloat(minVal) > 0 || parseFloat(maxVal) < 10000) {
            // Fallback: if slider not found but values exist
            filters.price = `${parseFloat(minVal).toFixed(2)}:${parseFloat(maxVal).toFixed(2)}`;
          }
        }
      }
    }
    
    // Extract sort value if set and map to API format
    const activeSortBtn = document.querySelector('.boost-pfs-filter-top-sorting .Popover__Value.active, .boost-pfs-filter-top-sorting .Popover__Value[aria-selected="true"], .boost-pfs-filter-top-sorting .Popover__Value.is-selected');
    if (activeSortBtn) {
      const sortValue = activeSortBtn.getAttribute('data-value');
      if (sortValue) {
        // Map sort values to API format (only the 4 needed: price-ascending, price-descending, best-selling, created-descending)
        const sortMap = {
          'price-ascending': 'price-ascending',
          'price-descending': 'price-descending',
          'best-selling': 'best-selling',
          'created-descending': 'created-descending',
          'date-descending': 'created-descending',
          'title-ascending': 'title-ascending',
          'title-descending': 'title-descending',
          'manual': 'manual',
          'featured': 'manual'
        };
        filters.sort = sortMap[sortValue] || sortValue;
      }
    } else {
      // If no active sort button, check URL parameter
      const urlParams = new URLSearchParams(window.location.search);
      const urlSort = urlParams.get('sort') || urlParams.get('sort_by');
      if (urlSort) {
        const sortMap = {
          'price-ascending': 'price-ascending',
          'price-descending': 'price-descending',
          'best-selling': 'best-selling',
          'created-descending': 'created-descending',
          'date-descending': 'created-descending',
          'title-ascending': 'title-ascending',
          'title-descending': 'title-descending',
          'manual': 'manual',
          'featured': 'manual'
        };
        filters.sort = sortMap[urlSort] || urlSort;
      }
    }
    
    // Also check refine-by section for already applied filters
    // This is important because sometimes filters are only shown in refine-by, not in filter tree
    // Check if refine-by section exists and has visible items
    const refineBySection = document.querySelector('.boost-pfs-filter-refine-by');
    const refineByItems = refineBySection ? 
      refineBySection.querySelectorAll('.refine-by-item') : 
      [];
    
    // Filter out hidden items
    const visibleRefineByItems = Array.from(refineByItems).filter(item => {
      return item.offsetParent !== null && 
             item.style.display !== 'none' && 
             !item.hasAttribute('style') || 
             !item.getAttribute('style')?.includes('display: none');
    });
    
    visibleRefineByItems.forEach(item => {
      const filterType = item.getAttribute('data-filter-type') || item.getAttribute('data-id');
      const filterValueEl = item.querySelector('.refine-by-type strong');
      const filterValue = filterValueEl ? filterValueEl.textContent.trim() : 
                         (item.getAttribute('data-value') || item.querySelector('a')?.textContent.trim());
      
      // Extract filter type from the refine-by-type span
      const refineByTypeEl = item.querySelector('.refine-by-type span');
      const filterTypeText = refineByTypeEl ? refineByTypeEl.textContent.trim().toLowerCase().replace(':', '') : '';
      
      if (filterValue) {
        // Determine filter type from text or attribute
        let filterKey = null;
        if (filterTypeText.includes('category') || filterTypeText.includes('product type') || filterTypeText.includes('type')) {
          filterKey = 'category';
        } else if (filterTypeText.includes('size')) {
          filterKey = 'size';
        } else if (filterTypeText.includes('color') || filterTypeText.includes('colour')) {
          filterKey = 'color';
        } else if (filterTypeText.includes('vendor') || filterTypeText.includes('brand')) {
          filterKey = 'vendor';
        }
        
        // Also check data attributes
        if (!filterKey && filterType) {
          const type = filterType.toLowerCase();
          if (type.includes('product_type') || type.includes('product-type') || type.includes('category')) {
            filterKey = 'category';
          } else if (type.includes('size')) {
            filterKey = 'size';
          } else if (type.includes('color')) {
            filterKey = 'color';
          } else if (type.includes('vendor')) {
            filterKey = 'vendor';
          }
        }
        
        if (filterKey) {
          if (!filters[filterKey]) filters[filterKey] = [];
          if (!filters[filterKey].includes(filterValue)) {
            filters[filterKey].push(filterValue);
          }
        }
      }
    });
    
    console.log('[Collection API] Extracted filters:', filters);
    return filters;
  }

  /**
   * Build URL parameters string from filters object
   * @param {Object} filters - Filters object
   * @returns {string} - URL parameters string
   */
  buildFilterParams(filters) {
    const params = [];
    
    // Product Type - pf_pt_product_type[] (pt = product_type)
    if (filters.product_type && filters.product_type.length > 0) {
      filters.product_type.forEach(type => {
        params.push(`pf_pt_product_type[]=${encodeURIComponent(type)}`);
      });
    }
    
    // Size - pf_opt_size[]
    if (filters.size && filters.size.length > 0) {
      filters.size.forEach(size => {
        params.push(`pf_opt_size[]=${encodeURIComponent(size)}`);
      });
    }
    
    // Color - pf_t_color[] (with COLOR_ prefix)
    if (filters.color && filters.color.length > 0) {
      filters.color.forEach(color => {
        // Boost PFS color format requires COLOR_ prefix
        const colorValue = color.startsWith('COLOR_') ? color : 'COLOR_' + color;
        params.push(`pf_t_color[]=${encodeURIComponent(colorValue)}`);
      });
    }
    
    // Vendor - pf_v_vendor[]
    if (filters.vendor && filters.vendor.length > 0) {
      filters.vendor.forEach(vendor => {
        params.push(`pf_v_vendor[]=${encodeURIComponent(vendor)}`);
      });
    }
    
    // Collection
    if (filters.collection && filters.collection.length > 0) {
      filters.collection.forEach(collection => {
        params.push(`pf_c_collection[]=${encodeURIComponent(collection)}`);
      });
    }
    
    // Tag
    if (filters.tag && filters.tag.length > 0) {
      filters.tag.forEach(tag => {
        params.push(`pf_t_tag[]=${encodeURIComponent(tag)}`);
      });
    }
    
    // Category - pf_pt_category[]
    if (filters.category && filters.category.length > 0) {
      filters.category.forEach(category => {
        params.push(`pf_pt_category[]=${encodeURIComponent(category)}`);
      });
    }
    
    // Price - pf_p_price[]
    if (filters.price) {
      params.push(`pf_p_price[]=${encodeURIComponent(filters.price)}`);
    }
    
    // Note: Sort is NOT added here because it's already included in baseParams in buildFilterUrl
    // Adding it here would cause duplicate sort parameters in the URL
    
    console.log('[Collection API] Filter params:', params.join('&'));
    return params.join('&');
  }

  /**
   * Perform collection filter with applied filters
   * @param {string} collectionHandle - Collection handle
   * @param {Object} filters - Applied filters
   */
  async performCollectionFilter(collectionHandle, filters) {
    const grid = document.querySelector(`[${this.COLLECTION_RESULTS_GRID_ATTR}]`) || 
                 document.querySelector('.boost-pfs-filter-products-new-bs');
    
    if (!grid) {
      console.warn('[Collection API] Grid not found');
      return;
    }

    // Reset state for new filter
    this.allProductHandles = [];
    this.currentDisplayCount = 0;
    this.totalProducts = 0;
    this.currentPage = 1;
    this.startingPage = 1;
    this.hasMorePages = true;
    
    // Remove page parameter from URL when filters are applied (reset to page 1)
    if (window.history && window.history.replaceState) {
      const url = new URL(window.location);
      url.searchParams.delete('page');
      window.history.replaceState({}, '', url);
    }
    
    // Remove any existing "View More" button
    const existingButton = grid.parentElement.querySelector('.collection-view-more-container');
    if (existingButton) {
      existingButton.remove();
    }

    // Show skeleton loader
    this.showSkeletonLoader(grid);

    try {
      // Build filter URL for pagination
      this.buildFilterUrl(collectionHandle, filters);
      
      // Fetch only first page (24 products) for fast loading
      const firstPageData = await this.fetchPage(collectionHandle, filters, 1);
      
      if (!firstPageData || firstPageData.handles.length === 0) {
        this.hideSkeletonLoader(grid);
        this.showEmptyState(grid);
        return;
      }

      // Store total products count
      this.totalProducts = firstPageData.totalProducts || 0;
      this.allProductHandles = firstPageData.handles;
      this.currentDisplayCount = 0;
      this.currentPage = 1;
      this.hasMorePages = firstPageData.handles.length === this.PRODUCTS_PER_PAGE && 
                         this.currentDisplayCount + firstPageData.handles.length < this.totalProducts;

      // Load initial batch of product cards (first 24 products)
      await this.loadProductCards(grid, this.allProductHandles, true);
      
      // Re-check hasMorePages after products loaded
      this.hasMorePages = this.currentDisplayCount < this.totalProducts;
      console.log('[Collection API] After initial load - displayed:', this.currentDisplayCount, 'total:', this.totalProducts, 'hasMorePages:', this.hasMorePages);
      
      // Trigger infinite scroll check
      setTimeout(() => this.checkAndLoadMore(), 500);

    } catch (error) {
      console.error("[Collection API] Error performing collection filter:", error);
      this.hideSkeletonLoader(grid);
      grid.innerHTML = `<div class="text-center p-5"><p class="text-danger">Error loading filtered results. Please try again.</p></div>`;
    }
  }

  /**
   * Build and store filter URL for pagination
   * @param {string} collectionHandle - Collection handle
   * @param {Object} filters - Applied filters
   */
  buildFilterUrl(collectionHandle, filters) {
    const filterParams = this.buildFilterParams(filters);
    const sortValue = filters.sort || (typeof boostPFSAppConfig !== 'undefined' && boostPFSAppConfig.general ? boostPFSAppConfig.general.default_sort_by : 'best-selling');
    const timestamp = Date.now();
    
    console.log('[Collection API] Building filter URL with sort:', sortValue, 'collection_scope (ID):', this.currentCollectionId);
    
    // Build base params matching the Boost PFS filter API format
    // Use collection_scope with collection ID for collection-specific filtering
    const collectionScope = this.currentCollectionId > 0 ? this.currentCollectionId : 0;
    
    const baseParams = [
      `t=${timestamp}`,
      `_=pf`,
      `shop=${this.SHOP_DOMAIN}`,
      `limit=${this.PRODUCTS_PER_PAGE}`,
      `display=grid`,
      `collection_scope=${collectionScope}`, // Collection ID for collection-specific filtering and sorting
      `event_type=filter`,
      `sort=${encodeURIComponent(sortValue)}`, // Sort is included here, NOT in buildFilterParams
      `tag=`,
      `product_available=true`,
      `variant_available=true`,
      `build_filter_tree=true`,
      `check_cache=false`,
      `sort_first=available`
    ].join('&');
    
    this.filterBaseUrl = `${this.API_BASE_URL}?${baseParams}${filterParams ? '&' + filterParams : ''}`;
    console.log('[Collection API] Complete Filter URL:', this.filterBaseUrl);
  }

  /**
   * Fetch a single page of products
   * @param {string} collectionHandle - Collection handle
   * @param {Object} filters - Applied filters
   * @param {number} page - Page number to fetch
   * @returns {Promise<Object>} - Object with handles array and totalProducts count
   */
  async fetchPage(collectionHandle, filters, page) {
    // Build URL if not already built
    if (!this.filterBaseUrl) {
      this.buildFilterUrl(collectionHandle, filters);
    }

    const pageUrl = this.filterBaseUrl + "&page=" + page;

    try {
      const response = await fetch(pageUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Filter request was not ok");
      }

      const data = await response.json();



            // Capture request ID (rid) for analytics tracking
      if (data && data.meta && data.meta.rid) {
        this.storeRequestIdForAnalytics('filter', data.meta.rid);
      } else if (data && data.rid) {
        this.storeRequestIdForAnalytics('filter', data.rid);
      }



      // Get total products count
      const totalProducts = typeof data.total_product === "number" ? data.total_product : 0;

      // Extract handles from current page
      const handles = this.extractProductHandles(data);

      return {
        handles: handles,
        totalProducts: totalProducts
      };
    } catch (error) {
      console.error(`[Collection API] Error loading page ${page}:`, error);
      return {
        handles: [],
        totalProducts: 0
      };
    }
  }

  /**
   * Fetch all products from all API pages with filters (kept for backward compatibility)
   * @param {string} collectionHandle - Collection handle
   * @param {Object} filters - Applied filters
   * @returns {Promise<Array>} - Array of all product handles
   */
  async fetchAllProductsWithFilters(collectionHandle, filters) {
    const filterParams = this.buildFilterParams(filters);
    const sortValue = filters.sort || (typeof boostPFSAppConfig !== 'undefined' && boostPFSAppConfig.general ? boostPFSAppConfig.general.default_sort_by : 'best-selling');
    const timestamp = Date.now();
    
    // Build base params matching the Boost PFS filter API format
    const baseParams = [
      `t=${timestamp}`,
      `collection_handle=${encodeURIComponent(collectionHandle)}`,
      `_=pf`,
      `shop=${this.SHOP_DOMAIN}`,
      `limit=${this.PRODUCTS_PER_PAGE}`,
      `sort=${sortValue}`,
      `display=grid`,
      `collection_scope=0`,
      `tag=`,
      `product_available=true`,
      `variant_available=true`,
      `build_filter_tree=true`,
      `check_cache=false`,
      `sort_first=available`,
      `event_type=filter`
    ].join('&');
    
    const filterBaseUrl = `${this.API_BASE_URL}?${baseParams}${filterParams ? '&' + filterParams : ''}`;
    
    console.log('[Collection API] Filter URL:', filterBaseUrl);
    
    let page = 1;
    let aggregatedHandles = [];
    let totalProducts = null;
    const MAX_PAGES = 40;

    const loadNextPage = async () => {
      const pageUrl = filterBaseUrl + "&page=" + page;

      try {
        const response = await fetch(pageUrl, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Filter request was not ok");
        }

        const data = await response.json();

        // Get total products count from first page
        if (typeof totalProducts !== "number") {
          totalProducts = typeof data.total_product === "number" ? data.total_product : 0;
          this.totalProducts = totalProducts;
        }

        // Extract handles from current page
        const pageHandles = this.extractProductHandles(data);
        if (pageHandles.length > 0) {
          aggregatedHandles = aggregatedHandles.concat(pageHandles);
        }

        // Check if we should load more pages
        const shouldLoadMore =
          aggregatedHandles.length < (totalProducts || 0) &&
          pageHandles.length > 0 &&
          page < MAX_PAGES;

        if (shouldLoadMore) {
          page += 1;
          return loadNextPage();
        }

        return aggregatedHandles;
      } catch (error) {
        console.error(`[Collection API] Error loading page ${page}:`, error);
        return aggregatedHandles;
      }
    };

    return loadNextPage();
  }




    /**
   * Store request ID for Boost Analytics tracking
   * @param {string} actionType - The action type (filter, search, suggest, recommend)
   * @param {string} rid - The request ID from API response
   */
  storeRequestIdForAnalytics(actionType, rid) {
    if (!rid) return;
    
    try {
      // Store in localStorage for analytics tracking
      const requestIdsKey = 'boostPFSRequestIds';
      let requestIds = {};
      
      const stored = localStorage.getItem(requestIdsKey);
      if (stored) {
        try {
          requestIds = JSON.parse(stored);
        } catch (e) {
          requestIds = {};
        }
      }
      
      requestIds[actionType] = {
        rid: rid,
        timestamp: Date.now()
      };
      
      localStorage.setItem(requestIdsKey, JSON.stringify(requestIds));
      
      // Also notify BoostAnalytics if available
      if (typeof window.BoostAnalytics !== 'undefined' && window.BoostAnalytics.setRequestId) {
        window.BoostAnalytics.setRequestId(actionType, rid);
      }
      
      console.log('[Collection API] Stored request ID for analytics:', actionType, rid);
    } catch (e) {
      console.warn('[Collection API] Failed to store request ID:', e);
    }
  }



  extractProductHandles(data) {
    const handles = [];
    
    if (data && data.products && Array.isArray(data.products)) {
      data.products.forEach((product) => {
        if (product.handle) {
          handles.push(product.handle);
        }
      });
    }
    
    return handles;
  }

  /**
   * Show skeleton loader with fading animation
   * @param {HTMLElement} grid - Grid container
   */
  showSkeletonLoader(grid) {
    const gridSettings = this.getGridSettings();
    const skeletonCount = 12; // Show 12 skeleton cards
    let skeletonHTML = '';
    
    for (let i = 0; i < skeletonCount; i++) {
      skeletonHTML += `
        <div class="Grid__Cell 1/${gridSettings.mobile}--phone 1/${gridSettings.tablet}--tablet-and-up 1/${gridSettings.desktop}--${gridSettings.deskClass} collection-skeleton-item">
          <div class="ProductItem">
            <div class="ProductItem__Wrapper">
              <div class="card-wrapper">
                <div class="ProductItem__ImageWrapper">
                  <div class="AspectRatio AspectRatio--withFallback" style="padding-bottom: 150%; --aspect-ratio: 0.6666666666666666; position: relative;">
                    <div class="collection-skeleton-image"></div>
                  </div>
                </div>
                <div class="available-variant-size custom-none">
                  <div class="collection-skeleton-sizes"></div>
                </div>
              </div>
              <div class="ProductItem__Info ProductItem__Info--center">
                <div class="ProductItem__TitlePriceRow">
                  <h2 class="ProductItem__Title Heading">
                    <div class="collection-skeleton-title"></div>
                  </h2>
                  <div class="ProductItem__PriceList Heading">
                    <div class="collection-skeleton-price"></div>
                  </div>
                </div>
                <div class="ProductItem__ColorSwatchRow">
                  <div class="collection-skeleton-swatches"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      `;
    }
    
    grid.innerHTML = skeletonHTML;
    
    // Inject skeleton styles if not already present
    this.injectSkeletonStyles();
  }

  /**
   * Hide skeleton loader
   * @param {HTMLElement} grid - Grid container
   */
  hideSkeletonLoader(grid) {
    const skeletonItems = grid.querySelectorAll('.collection-skeleton-item');
    skeletonItems.forEach(item => {
      item.style.opacity = '0';
      item.style.transition = 'opacity 0.3s ease-out';
    });
    
    // Remove skeleton items after fade out
    setTimeout(() => {
      skeletonItems.forEach(item => item.remove());
    }, 300);
  }

  /**
   * Inject skeleton loader CSS styles
   */
  injectSkeletonStyles() {
    // Check if styles are already injected
    if (document.getElementById('collection-skeleton-styles')) {
      return;
    }
    
    const style = document.createElement('style');
    style.id = 'collection-skeleton-styles';
    style.textContent = `
      .collection-skeleton-item {
        animation: collectionSkeletonFade 1.5s ease-in-out infinite;
      }
      
      @keyframes collectionSkeletonFade {
        0%, 100% {
          opacity: 1;
        }
        50% {
          opacity: 0.6;
        }
      }
      
      .collection-skeleton-image {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        min-height: 200px;
        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
        background-size: 200% 100%;
        animation: collectionSkeletonShimmer 1.5s infinite;
        border-radius: 0;
        z-index: 1;
      }
      
      .collection-skeleton-item .AspectRatio {
        position: relative;
        min-height: 200px;
      }
      
      .collection-skeleton-item .ProductItem__ImageWrapper {
        display: block;
      }
      
      .collection-skeleton-sizes {
        height: 30px;
        margin: 10px 0;
        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
        background-size: 200% 100%;
        animation: collectionSkeletonShimmer 1.5s infinite;
        border-radius: 4px;
      }
      
      .collection-skeleton-title {
        height: 20px;
        width: 70%;
        margin: 10px 0;
        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
        background-size: 200% 100%;
        animation: collectionSkeletonShimmer 1.5s infinite;
        border-radius: 4px;
      }
      
      .collection-skeleton-price {
        height: 18px;
        width: 50%;
        margin: 5px 0;
        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
        background-size: 200% 100%;
        animation: collectionSkeletonShimmer 1.5s infinite;
        border-radius: 4px;
      }
      
      .collection-skeleton-swatches {
        height: 25px;
        width: 60%;
        margin: 10px 0;
        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
        background-size: 200% 100%;
        animation: collectionSkeletonShimmer 1.5s infinite;
        border-radius: 4px;
      }
      
      @keyframes collectionSkeletonShimmer {
        0% {
          background-position: 200% 0;
        }
        100% {
          background-position: -200% 0;
        }
      }
    `;
    
    document.head.appendChild(style);
  }

  /**
   * Get grid settings from the collection page
   * @returns {Object} - Grid settings with mobile, tablet, desktop counts and desk class
   */
  getGridSettings() {
    const grid = document.querySelector(`[${this.COLLECTION_RESULTS_GRID_ATTR}]`) || 
                 document.querySelector('.boost-pfs-filter-products-new-bs');
    
    if (!grid) {
      // Default values
      return {
        mobile: '2',
        tablet: '3',
        desktop: '4',
        deskClass: 'desk'
      };
    }
    
    // Get from data attributes
    const mobileCount = grid.getAttribute('data-mobile-count') || '2';
    const desktopCount = grid.getAttribute('data-desktop-count') || '4';
    
    // Calculate tablet count (same logic as collection template)
    const tabletCount = desktopCount === '4' ? '3' : '2';
    
    // Determine desk class based on filter position
    // Check if filter position is drawer (lap-and-up) or sidebar (desk)
    const filterPosition = typeof boostPFSThemeConfig !== 'undefined' && 
                          boostPFSThemeConfig.custom && 
                          boostPFSThemeConfig.custom.filter_position 
                          ? boostPFSThemeConfig.custom.filter_position 
                          : 'sidebar';
    
    const deskClass = filterPosition === 'drawer' ? 'lap-and-up' : 'desk';
    
    return {
      mobile: mobileCount,
      tablet: tabletCount,
      desktop: desktopCount,
      deskClass: deskClass
    };
  }

  /**
   * Wrap product HTML in Grid__Cell div with correct classes
   * @param {string} productHtml - Product HTML from template
   * @param {string} productHandle - Product handle for ID attribute
   * @returns {string} - Wrapped product HTML
   */
  wrapProductInGridCell(productHtml, productHandle) {
    const gridSettings = this.getGridSettings();
    
    // Extract product ID from HTML
    let productId = null;
    try {
      // Try to extract product ID from data-product-id attribute in the HTML
      const productIdMatch = productHtml.match(/data-product-id="(\d+)"/);
      if (productIdMatch && productIdMatch[1]) {
        productId = productIdMatch[1];
      } else {
        // Try alternative pattern
        const altMatch = productHtml.match(/data-id="(\d+)"/);
        if (altMatch && altMatch[1]) {
          productId = altMatch[1];
        }
      }
    } catch (e) {
      console.warn('[Collection API] Could not extract product ID:', e);
    }
    
    // Build Grid__Cell classes (matching collection template format)
    const gridCellClasses = `Grid__Cell 1/${gridSettings.mobile}--phone 1/${gridSettings.tablet}--tablet-and-up 1/${gridSettings.desktop}--${gridSettings.deskClass}`;
    
    // Add asl_processed class if product ID exists (matching original format)
    const additionalClasses = productId ? ' asl_processed' : '';
    
    // Add ID attribute using product handle (for scroll restoration)
    // Use a prefix to avoid conflicts and ensure it's not in URL
    const productHandleId = productHandle ? `id="product-${productHandle}"` : '';
    const productHandleDataAttr = productHandle ? `data-product-handle="${productHandle}"` : '';
    
    // Wrap the product HTML
    const wrappedHtml = `<div class="${gridCellClasses}${additionalClasses}"${productHandleId ? ` ${productHandleId}` : ''}${productHandleDataAttr ? ` ${productHandleDataAttr}` : ''}${productId ? ` data-id="${productId}"` : ''}>${productHtml}</div>`;
    
    return wrappedHtml;
  }

  loadProductCards(grid, handles, isInitialLoad = false) {
    return new Promise((resolve, reject) => {
      const requests = handles.map((handle) => {
        // Try collection view first, fallback to search view
        const productTileTemplateUrl = `/products/${handle}?view=collection`;
        return fetch(productTileTemplateUrl)
          .then((res) => {
            if (res.status === 200) {
              return res.text();
            }
            // If collection view doesn't exist, try search view
            if (res.status === 404) {
              return fetch(`/products/${handle}?view=search`)
                .then((searchRes) => {
                  if (searchRes.status === 200) {
                    return searchRes.text();
                  }
                  return null;
                });
            }
            return null;
          })
          .catch((error) => {
            console.error(`[Collection API] Error loading product ${handle}:`, error);
            // Try search view as fallback
            return fetch(`/products/${handle}?view=search`)
              .then((searchRes) => {
                if (searchRes.status === 200) {
                  return searchRes.text();
                }
                return null;
              })
              .catch(() => null);
          });
      });

      Promise.all(requests).then((responses) => {
        const validResponses = responses.filter((r) => r !== null);
        
        if (validResponses.length === 0) {
          if (isInitialLoad) {
            this.showEmptyState(grid);
          }
          resolve();
          return;
        }

        // Wrap each product in Grid__Cell div with product handle for ID
        const wrappedProducts = validResponses.map((productHtml, index) => {
          const handle = handles[index] || null;
          return this.wrapProductInGridCell(productHtml, handle);
        });
        
        const productCards = wrappedProducts.join("");
        
        if (isInitialLoad) {
          // Hide skeleton first, then replace with products
          this.hideSkeletonLoader(grid);
          
          // Wait for skeleton fade out, then show products
          setTimeout(() => {
            grid.innerHTML = productCards;
            this.currentDisplayCount = validResponses.length;
            
            // Mark initial API load as complete (for bfcache handling)
            this.initialAPILoadComplete = true;
            
            // Trigger any necessary initialization for the product cards
            this.initializeProductCards(grid);
            
            // Add or update "View More" button
            this.updateViewMoreButton();
            
            // Restore scroll position to clicked product if returning from product page
            // Check if this is a woet page (needs longer delay)
            const isWoetPage = window.location.search.includes('style=woet');
            const delay = isWoetPage ? 1500 : 800; // Longer delay for woet pages
            
            console.log('[Collection API] Will restore scroll after', delay, 'ms (woet page:', isWoetPage, ')');
            
            setTimeout(() => {
              this.restoreScrollToProduct();
            }, delay);
            
            resolve();
          }, 300); // Wait for skeleton fade out animation
        } else {
          // Append to existing content for "View More"
          grid.insertAdjacentHTML('beforeend', productCards);
          this.currentDisplayCount += validResponses.length;
          
          // Trigger any necessary initialization for the product cards
          this.initializeProductCards(grid);
          
          // Add or update "View More" button (will hide if no more products, or recreate if more available)
          this.updateViewMoreButton();
          
          resolve();
        }
      }).catch((error) => {
        console.error('[Collection API] Error in loadProductCards:', error);
        reject(error);
      });
    });
  }

  updateViewMoreButton() {
    const grid = document.querySelector(`[${this.COLLECTION_RESULTS_GRID_ATTR}]`) || 
                 document.querySelector('.boost-pfs-filter-products-new-bs');
    if (!grid) return;
    
    // Remove existing button container if any - we use infinite scroll instead
    const existingContainer = grid.parentElement.querySelector('.collection-view-more-container');
    if (existingContainer) {
      existingContainer.remove();
    }
    
    // Using infinite scroll - don't create View More button
    // Just trigger a check in case we need to load more immediately
    if (this.hasMorePages) {
      setTimeout(() => this.checkAndLoadMore(), 100);
    }
    
    return; // Don't create View More button - infinite scroll handles loading
    
    // Check if there are more products to show
    let hasMoreProducts = false;
    
    if (this.totalProducts > 0) {
      // We have total count - check if displayed count is less than total
      hasMoreProducts = this.currentDisplayCount < this.totalProducts;
    } else {
      // No total count yet - check if we have more pages
      hasMoreProducts = this.hasMorePages;
    }
    
    // Also verify we have a filter URL to load more (or collection handle for initial load)
    if (hasMoreProducts && !this.filterBaseUrl && !this.currentCollectionHandle) {
      hasMoreProducts = false;
    }
    
    // Final check: hasMorePages must be true to show button
    // This ensures the button is hidden when we've reached the end
    if (hasMoreProducts && !this.hasMorePages) {
      hasMoreProducts = false;
    }
    
    console.log('[Collection API] View More button check:', {
      currentDisplayCount: this.currentDisplayCount,
      totalProducts: this.totalProducts,
      allProductHandles: this.allProductHandles.length,
      hasMorePages: this.hasMorePages,
      hasFilterBaseUrl: !!this.filterBaseUrl,
      hasMoreProducts: hasMoreProducts
    });
    
    if (hasMoreProducts) {
      // Create "View More" button
      const buttonContainer = document.createElement('div');
      buttonContainer.className = 'collection-view-more-container text-center';
      buttonContainer.style.cssText = '';
      
      const viewMoreButton = document.createElement('button');
      viewMoreButton.className = 'collection-view-more-button button button--medium';
      viewMoreButton.textContent = `View More`;
      viewMoreButton.type = 'button';
      
      viewMoreButton.addEventListener('click', () => {
        this.loadMoreProducts();
      });
      
      buttonContainer.appendChild(viewMoreButton);
      grid.parentElement.appendChild(buttonContainer);
      
      console.log('[Collection API] View More button created');
    } else {
      console.log('[Collection API] View More button hidden - no more products');
    }
  }

  /**
   * Add/remove footer logo loading class
   * @param {boolean} add - If true, add the class; if false, remove it
   */
  toggleFooterLogoLoading(add) {
    const footerLogoImg = document.querySelector(".Footer__Block.Footer__Block--logo img");
    const footerLogoImgview = document.querySelector(".collection-view-more-button");
    if (footerLogoImg) {
      if (add) {
        footerLogoImg.classList.add('footer-logo-loading');
         footerLogoImgview.classList.add('footer-logo-loading-new');
      } else {
        footerLogoImg.classList.remove('footer-logo-loading');
        footerLogoImgview.classList.remove('footer-logo-loading-new');
      }
    }
  }

  async loadMoreProducts() {
    const grid = document.querySelector(`[${this.COLLECTION_RESULTS_GRID_ATTR}]`) || 
                 document.querySelector('.boost-pfs-filter-products-new-bs');
    if (!grid) return;
    
    const viewMoreButton = grid.parentElement.querySelector('.collection-view-more-button');
    let originalText = 'View More';
    
    // Add footer logo loading class
    this.toggleFooterLogoLoading(true);
    
    // Remove footer logo loading class after 2 seconds
    setTimeout(() => {
      this.toggleFooterLogoLoading(false);
    }, 5000);
    
    // Disable button and show loading state
    if (viewMoreButton) {
      viewMoreButton.disabled = true;
       viewMoreButton.style.display = 'none';
      originalText = viewMoreButton.textContent;
      viewMoreButton.textContent = '';
    }
    
    // Check if we have more pages to load
    if (!this.hasMorePages || !this.filterBaseUrl) {
      console.log('[Collection API] No more pages to load. hasMorePages:', this.hasMorePages, 'filterBaseUrl:', !!this.filterBaseUrl);
      // Re-enable button and let updateViewMoreButton handle hiding it
      if (viewMoreButton) {
        viewMoreButton.disabled = false;
        viewMoreButton.textContent = originalText;
      }
      this.updateViewMoreButton();
      return;
    }

    try {
      // Fetch next page from API
      const nextPage = this.currentPage + 1;
      console.log('[Collection API] Loading page', nextPage, 'of products. Current display count:', this.currentDisplayCount, 'Total products:', this.totalProducts);
      
      // Update URL with page parameter
      if (window.history && window.history.pushState) {
        const url = new URL(window.location);
        url.searchParams.set('page', nextPage);
        window.history.pushState({}, '', url);
      }
      
      const pageData = await this.fetchPage(this.currentCollectionHandle, this.currentFilters, nextPage);
      
      if (!pageData || pageData.handles.length === 0) {
        // No more products available
        this.hasMorePages = false;
        console.log('[Collection API] No more products to load - received empty page');
        
        // Re-enable button and let updateViewMoreButton hide it
        if (viewMoreButton) {
          viewMoreButton.disabled = false;
          viewMoreButton.textContent = originalText;
        }
        this.updateViewMoreButton();
        return;
      }

      // Add new handles to our collection
      this.allProductHandles = this.allProductHandles.concat(pageData.handles);
      this.currentPage = nextPage;
      // console.log(allProductHandles , 'allProductHandles')
      // console.log(allProductHandles.length , 'allProductHandles lenbght')
      
      // Update total products if we got a new value
      if (pageData.totalProducts > 0) {
        this.totalProducts = pageData.totalProducts;
      }
      
      // Load the new batch of product cards (this will update currentDisplayCount)
      await this.loadProductCards(grid, pageData.handles, false);
      
      // Small delay to ensure DOM is updated
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // After products are loaded, check if there are more pages
      // Calculate based on what we've displayed vs total
      const totalDisplayedAfterLoad = this.currentDisplayCount;
      const hasMoreAfterLoad = this.totalProducts > 0 ? totalDisplayedAfterLoad < this.totalProducts : true;
      
      // Also check if we got a full page (means there might be more)
      const gotFullPage = pageData.handles.length === this.PRODUCTS_PER_PAGE;
      
      // Update hasMorePages flag
      if (this.totalProducts > 0) {
        // We know the total - check if we've displayed less than total
        this.hasMorePages = totalDisplayedAfterLoad < this.totalProducts;
      } else {
        // Don't know total yet - assume more if we got a full page
        this.hasMorePages = gotFullPage;
      }
      
      // Additional check: if we got fewer products than PRODUCTS_PER_PAGE, we've reached the end
      if (pageData.handles.length < this.PRODUCTS_PER_PAGE) {
        this.hasMorePages = false;
        console.log('[Collection API] Reached end - got fewer products than expected:', pageData.handles.length, '<', this.PRODUCTS_PER_PAGE);
      }
      
      // Final check: if displayed count equals or exceeds total, no more pages
      if (this.totalProducts > 0 && totalDisplayedAfterLoad >= this.totalProducts) {
        this.hasMorePages = false;
        console.log('[Collection API] Reached end - displayed all products:', totalDisplayedAfterLoad, '>=', this.totalProducts);
      }
      
      console.log('[Collection API] After loading page', nextPage, ':', {
        loadedThisPage: pageData.handles.length,
        totalDisplayed: totalDisplayedAfterLoad,
        totalProducts: this.totalProducts,
        hasMoreAfterLoad: hasMoreAfterLoad,
        gotFullPage: gotFullPage,
        hasMorePages: this.hasMorePages
      });

      // updateViewMoreButton is already called by loadProductCards, but call it again to ensure button state is correct
      // This will show the button if there are more products, or hide it if all are loaded
      this.updateViewMoreButton();
      
    } catch (error) {
      console.error("[Collection API] Error loading more products:", error);
      
      // Re-enable button on error
      if (viewMoreButton) {
        viewMoreButton.disabled = false;
        viewMoreButton.textContent = originalText;
      }
      // Update button to show it again if there are more products
      this.updateViewMoreButton();
    }
  }

  initializeProductCards(grid) {
    // Dispatch event to notify that collection results have been loaded
    document.dispatchEvent(new CustomEvent("collection-results-loaded", {
      detail: { container: grid }
    }));
    
    // Initialize image sliders for newly loaded products
    if (typeof window.initAllImageSliders === 'function') {
      setTimeout(() => {
        window.initAllImageSliders();
      }, 100);
    }
    
    // Re-initialize wishlist buttons if they exist
    const wishlistButtons = grid.querySelectorAll("wishlist-item");
    if (wishlistButtons.length > 0) {
      wishlistButtons.forEach(btn => {
        btn.dispatchEvent(new CustomEvent("initialize", { bubbles: true }));
      });
    }
  }

  showEmptyState(grid) {
    grid.innerHTML = "";
    // You can add empty state HTML here if needed
  }

  /**
   * Setup continuous scroll tracking to store scroll position while user scrolls
   * This ensures we always have the latest scroll position even if user doesn't click a product
   */
  setupContinuousScrollTracking() {
    // Only track on collection pages
    if (!window.location.pathname.includes('/collections/')) {
      return;
    }
    
    let scrollTimeout = null;
    const SCROLL_DEBOUNCE_MS = 150; // Debounce scroll tracking to avoid excessive writes
    
    const saveScrollPosition = () => {
      try {
        // Get current scroll position (support multiple methods for mobile)
        const scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
        const scrollX = window.scrollX || window.pageXOffset || document.documentElement.scrollLeft || document.body.scrollLeft || 0;
        
        // Get current collection URL
        const collectionUrl = window.location.pathname + window.location.search;
        
        // Get or create scroll data
        let scrollData = null;
        const existingData = sessionStorage.getItem('collectionScrollData');
        if (existingData) {
          try {
            scrollData = JSON.parse(existingData);
            // Only update if we're on the same collection
            if (scrollData.collectionUrl && scrollData.collectionUrl.split('?')[0] === collectionUrl.split('?')[0]) {
              scrollData.scrollY = scrollY;
              scrollData.scrollX = scrollX;
              scrollData.timestamp = Date.now();
            } else {
              // Different collection, create new data
              scrollData = {
                scrollY: scrollY,
                scrollX: scrollX,
                collectionUrl: collectionUrl,
                timestamp: Date.now()
              };
            }
          } catch (e) {
            // Invalid data, create new
            scrollData = {
              scrollY: scrollY,
              scrollX: scrollX,
              collectionUrl: collectionUrl,
              timestamp: Date.now()
            };
          }
        } else {
          // No existing data, create new
          scrollData = {
            scrollY: scrollY,
            scrollX: scrollX,
            collectionUrl: collectionUrl,
            timestamp: Date.now()
          };
        }
        
        // Store in sessionStorage
        sessionStorage.setItem('collectionScrollData', JSON.stringify(scrollData));
      } catch (error) {
        console.warn('[Collection API] Error saving scroll position:', error);
      }
    };
    
    // Track scroll with debouncing
    const handleScroll = () => {
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      scrollTimeout = setTimeout(saveScrollPosition, SCROLL_DEBOUNCE_MS);
    };
    
    // Listen for scroll events
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Also save on page visibility change (when user might navigate away)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        saveScrollPosition();
      }
    });
    
    // Save before page unload (for Chrome/page refresh)
    window.addEventListener('beforeunload', saveScrollPosition);
    
    // Save on pagehide (for Safari bfcache)
    window.addEventListener('pagehide', saveScrollPosition);
    
    // Initial save
    saveScrollPosition();
  }

  /**
   * Setup product link click tracking to store product handle for scroll restoration
   */
  setupProductLinkTracking() {
    // Use event delegation to catch all product link clicks (both click and touch events for mobile)
    const handleProductClick = (e) => {
      // Check if clicked element is a product link (image or title)
      let productLink = e.target.closest('a.ProductItem__ImageWrapper, .ProductItem__Title a, a[href*="/products/"]');
      
      // Also check if the clicked element itself is a link
      if (!productLink && (e.target.tagName === 'A' || e.target.closest('a'))) {
        const link = e.target.tagName === 'A' ? e.target : e.target.closest('a');
        if (link && link.href && link.href.includes('/products/')) {
          productLink = link;
        }
      }
      
      if (productLink && productLink.href) {
        try {
          // Extract product handle from URL
          const productUrl = new URL(productLink.href, window.location.origin);
          const productHandleMatch = productUrl.pathname.match(/\/products\/([^\/\?]+)/);
          
          if (productHandleMatch && productHandleMatch[1]) {
            const productHandle = productHandleMatch[1];
            
            // Store only the product handle and collection URL
            const scrollData = {
              productHandle: productHandle,
              collectionUrl: window.location.pathname + window.location.search,
              timestamp: Date.now()
            };
            
            // Store in sessionStorage
            sessionStorage.setItem('collectionScrollData', JSON.stringify(scrollData));
            
            console.log('[Collection API] Stored product handle for restoration:', productHandle);
          }
        } catch (error) {
          console.warn('[Collection API] Error storing product handle:', error);
        }
      }
    };
    
    // Listen for both click and touchstart events (mobile support)
    document.addEventListener('click', handleProductClick, true); // Use capture phase
    document.addEventListener('touchstart', handleProductClick, true); // Mobile touch support
  }

  /**
   * Check if we need to restore scroll position after page load
   */
  checkScrollRestoration() {
    // Check if we're returning to the same collection page
    const scrollDataStr = sessionStorage.getItem('collectionScrollData');
    
    if (scrollDataStr) {
      try {
        const scrollData = JSON.parse(scrollDataStr);
        const currentUrl = window.location.pathname + window.location.search;
        const storedPath = scrollData.collectionUrl.split('?')[0];
        const currentPath = currentUrl.split('?')[0];
        
        if (storedPath === currentPath && storedPath.includes('/collections/')) {
          // We're on the same collection, will restore scroll after products load
          console.log('[Collection API] Will restore scroll position after products load');
        } else {
          // Different page, clear stored data
          sessionStorage.removeItem('collectionScrollData');
        }
      } catch (e) {
        console.warn('[Collection API] Error parsing scroll data:', e);
        sessionStorage.removeItem('collectionScrollData');
      }
    }
  }


  /**
   * Restore scroll position to the clicked product - scrollIntoView approach
   * Uses scrollIntoView with behavior auto and block start for consistent alignment
   * NOTE: Page parameter restoration is now handled by pageshow handler to avoid duplicates
   */
  restoreScrollToProduct() {
    const scrollDataStr = sessionStorage.getItem('collectionScrollData');
    
    if (!scrollDataStr) {
      return;
    }
    
    let scrollData;
    try {
      scrollData = JSON.parse(scrollDataStr);
    } catch (e) {
      sessionStorage.removeItem('collectionScrollData');
      return;
    }
    
    // Verify we're on the same collection (pathname match)
    const storedPath = scrollData.collectionUrl ? scrollData.collectionUrl.split('?')[0] : '';
    const currentPath = window.location.pathname;
    
    if (storedPath !== currentPath) {
      sessionStorage.removeItem('collectionScrollData');
      return;
    }
    
    // Skip if data is too old (30 minutes)
    if (scrollData.timestamp && (Date.now() - scrollData.timestamp > 30 * 60 * 1000)) {
      sessionStorage.removeItem('collectionScrollData');
      return;
    }
    
    // Need productHandle to scroll to
    if (!scrollData.productHandle) {
      sessionStorage.removeItem('collectionScrollData');
      return;
    }
    
    const productHandle = scrollData.productHandle;
    const productId = `product-${productHandle}`;
    
    // Use interval to retry until product element is found
    const interval = setInterval(() => {
      const target = document.getElementById(productId);
      
      if (target) {
        target.scrollIntoView({
          behavior: "auto",
          block: "start"
        });
        
        setTimeout(() => {
          sessionStorage.removeItem('collectionScrollData');
        }, 1000);
        
        clearInterval(interval);
      }
    }, 100);
    
    setTimeout(() => {
      clearInterval(interval);
      if (!document.getElementById(productId)) {
        console.warn('[Collection API] Product element not found after max retries:', productId);
        sessionStorage.removeItem('collectionScrollData');
      }
    }, 10000);
  }
}

customElements.define("collection-api", CollectionAPI);

// Initialize on DOM ready
document.addEventListener("DOMContentLoaded", function () {
  // Only initialize on collection pages
  if (window.location.pathname.includes('/collections/')) {
    const collectionAPI = document.querySelector("collection-api");
    if (!collectionAPI) {
      // Create and append the custom element if it doesn't exist
      const collectionApiElement = document.createElement("collection-api");
      collectionApiElement.setAttribute("data-shop-domain", 
        typeof boostPFSAppConfig !== 'undefined' && boostPFSAppConfig.shop 
          ? boostPFSAppConfig.shop.domain 
          : 'elie-tahari.myshopify.com'
      );
      document.body.appendChild(collectionApiElement);
    }
  }
});

// Standalone scroll restoration function - works without collection-api element
// NOTE: Page parameter restoration is handled by pageshow handler - this only does scroll
function restoreScrollToProductStandalone() {
  const scrollDataStr = sessionStorage.getItem('collectionScrollData');
  
  if (!scrollDataStr) {
    return;
  }
  
  let scrollData;
  try {
    scrollData = JSON.parse(scrollDataStr);
  } catch (e) {
    sessionStorage.removeItem('collectionScrollData');
    return;
  }
  
  const storedPath = scrollData.collectionUrl ? scrollData.collectionUrl.split('?')[0] : '';
  const currentPath = window.location.pathname;
  
  if (storedPath !== currentPath) {
    sessionStorage.removeItem('collectionScrollData');
    return;
  }
  
  if (scrollData.timestamp && (Date.now() - scrollData.timestamp > 30 * 60 * 1000)) {
    sessionStorage.removeItem('collectionScrollData');
    return;
  }
  
  if (!scrollData.productHandle) {
    sessionStorage.removeItem('collectionScrollData');
    return;
  }
  
  const productHandle = scrollData.productHandle;
  const productId = `product-${productHandle}`;
  
  const interval = setInterval(() => {
    const target = document.getElementById(productId);
    
    if (target) {
      target.scrollIntoView({
        behavior: "auto",
        block: "start"
      });
      
      setTimeout(() => {
        sessionStorage.removeItem('collectionScrollData');
      }, 1000);
      
      clearInterval(interval);
    }
  }, 100);
  
  setTimeout(() => {
    clearInterval(interval);
    if (!document.getElementById(productId)) {
      console.warn('[Collection API] Standalone - Product element not found after max retries:', productId);
      sessionStorage.removeItem('collectionScrollData');
    }
  }, 10000);
}

// Handle Safari/iOS bfcache (back/forward cache) restoration
// When user navigates back, Safari restores the page from cache without reloading
// Also handles Chrome page refresh scenario
window.addEventListener("pageshow", function (event) {
  // Only handle on collection pages
  if (window.location.pathname.includes('/collections/')) {
    // BFCACHE HANDLING: Safari bfcache restoration
    // When Safari restores from bfcache, the DOM shows cached products.
    // We show skeleton immediately and trigger fresh API load.
    // Scroll restoration happens automatically in loadProductCards after products load.
    if (event.persisted) {
      console.log('[Collection API] Bfcache restoration detected');
      
      const grid =
        document.querySelector('[grid-collection]') ||
        document.querySelector('.boost-pfs-filter-products-new-bs') ||
        document.querySelector('#collection-products-grid');
      
      const collectionAPIEl = document.querySelector('collection-api');
      
      if (!grid || !collectionAPIEl) {
        console.log('[Collection API] Bfcache: Grid or API element not found');
        return;
      }
      
      // If skeleton is already showing (stuck state), just reload
      if (grid.querySelector('.collection-skeleton-item')) {
        console.log('[Collection API] Bfcache: Skeleton stuck, reloading');
        window.location.reload();
        return;
      }
      
      // STEP 1: Restore URL parameters from scroll data BEFORE showing skeleton
      const scrollDataStr = sessionStorage.getItem('collectionScrollData');
      if (scrollDataStr) {
        try {
          const scrollData = JSON.parse(scrollDataStr);
          const storedPath = scrollData.collectionUrl ? scrollData.collectionUrl.split('?')[0] : '';
          const currentPath = window.location.pathname;
          
          if (storedPath === currentPath && scrollData.collectionUrl) {
            const storedUrl = new URL(scrollData.collectionUrl, window.location.origin);
            const currentUrl = new URL(window.location.href);
            
            // Restore all URL params from stored URL
            storedUrl.searchParams.forEach((value, key) => {
              if (!currentUrl.searchParams.has(key)) {
                currentUrl.searchParams.set(key, value);
              }
            });
            
            if (currentUrl.toString() !== window.location.href) {
              console.log('[Collection API] Bfcache: Restoring URL params');
              window.history.replaceState(null, '', currentUrl.toString());
            }
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
      
      // STEP 2: Show skeleton IMMEDIATELY (hides cached products)
      console.log('[Collection API] Bfcache: Showing skeleton and triggering fresh load');
      collectionAPIEl.showSkeletonLoader(grid);
      
      // STEP 3: Reset state and trigger fresh API load
      // DON'T clear scroll data - loadProductCards will use it for scroll restoration
      collectionAPIEl.initialAPILoadComplete = false;
      collectionAPIEl.currentDisplayCount = 0;
      collectionAPIEl.allProductHandles = [];
      
      // Trigger the API load (this will also call restoreScrollToProduct after products load)
      collectionAPIEl.initializeViewMoreForInitialProducts();
      
      // Exit - don't run non-bfcache logic
      return;
    }
    
    // NON-BFCACHE: Normal page load (Chrome back, or fresh navigation)
    const scrollDataStr = sessionStorage.getItem('collectionScrollData');
    
    if (scrollDataStr) {
      try {
        const scrollData = JSON.parse(scrollDataStr);
        const storedPath = scrollData.collectionUrl ? scrollData.collectionUrl.split('?')[0] : '';
        const currentPath = window.location.pathname;
        
        // Verify we're on the same collection
        if (storedPath === currentPath) {
          const storedUrl = new URL(scrollData.collectionUrl, window.location.origin);
          const storedPage = storedUrl.searchParams.get('page');
          const currentUrl = new URL(window.location.href);
          const currentPage = currentUrl.searchParams.get('page');
          
          // If stored URL had a page parameter but current URL doesn't, restore it
          if (storedPage && !currentPage) {
            console.log('[Collection API] Pageshow - Restoring page parameter:', storedPage);
            currentUrl.searchParams.set('page', storedPage);
            storedUrl.searchParams.forEach((value, key) => {
              if (key !== 'page' || !currentUrl.searchParams.has(key)) {
                currentUrl.searchParams.set(key, value);
              }
            });
            window.history.replaceState(null, '', currentUrl.toString());
            
            // For non-bfcache, manually trigger page loading
            setTimeout(() => {
              const checkUrl = new URL(window.location.href);
              const checkPage = checkUrl.searchParams.get('page');
              if (!checkPage || checkPage !== storedPage) {
                console.warn('[Collection API] URL not updated correctly, reloading');
                window.location.reload();
                return;
              }
              
              const collectionAPI = document.querySelector("collection-api");
              if (collectionAPI && collectionAPI.initializeViewMoreForInitialProducts) {
                console.log('[Collection API] Manually triggering page loading after URL restoration, page:', storedPage);
                collectionAPI.initializeViewMoreForInitialProducts().catch(err => {
                  console.error('[Collection API] Error loading pages after URL restoration:', err);
                  window.location.reload();
                });
              } else {
                setTimeout(() => {
                  const collectionAPI2 = document.querySelector("collection-api");
                  if (collectionAPI2 && collectionAPI2.initializeViewMoreForInitialProducts) {
                    collectionAPI2.initializeViewMoreForInitialProducts().catch(err => {
                      window.location.reload();
                    });
                  } else {
                    window.location.reload();
                  }
                }, 500);
              }
            }, 300);
          }
          
          // Scroll restoration for non-bfcache
          setTimeout(() => {
            const collectionAPI = document.querySelector("collection-api");
            if (collectionAPI && collectionAPI.restoreScrollToProduct) {
              collectionAPI.restoreScrollToProduct();
            } else {
              restoreScrollToProductStandalone();
            }
          }, 100);
        } else {
          sessionStorage.removeItem('collectionScrollData');
        }
      } catch (e) {
        sessionStorage.removeItem('collectionScrollData');
      }
    }
  }
});

// Also listen to visibilitychange for mobile Safari (when page becomes visible)
document.addEventListener("visibilitychange", function() {
  if (document.visibilityState === 'visible' && window.location.pathname.includes('/collections/')) {
    const scrollDataStr = sessionStorage.getItem('collectionScrollData');
    if (scrollDataStr) {
      try {
        const scrollData = JSON.parse(scrollDataStr);
        const storedPath = scrollData.collectionUrl.split('?')[0];
        const currentPath = window.location.pathname;
        
        if (storedPath === currentPath) {
          // Small delay for mobile Safari
          setTimeout(() => {
            restoreScrollToProductStandalone();
          }, 300);
        }
      } catch (e) {
        // Ignore
      }
    }
  }
});

// Also listen to focus event for mobile Safari
window.addEventListener("focus", function() {
  if (window.location.pathname.includes('/collections/')) {
    const scrollDataStr = sessionStorage.getItem('collectionScrollData');
    if (scrollDataStr) {
      try {
        const scrollData = JSON.parse(scrollDataStr);
        const storedPath = scrollData.collectionUrl.split('?')[0];
        const currentPath = window.location.pathname;
        
        if (storedPath === currentPath) {
          setTimeout(() => {
            restoreScrollToProductStandalone();
          }, 300);
        }
      } catch (e) {
        // Ignore
      }
    }
  }
});
